import { Search } from "baseui/icon";
import classNames from "classnames";
import React, { FC } from "react";
import { HiXMark } from "react-icons/hi2";
import { IoFilterOutline } from "react-icons/io5";

import Button from "../../../components/button/Button";
import CustomSelect from "../../../components/select/Select";
import { SelectOptionType } from "../../../types/types";
import { WorksFilters } from "../types/work-filters";
import { clearWorksFilters, hasActiveWorksFilters } from "../utils/workFilters";

type ShowFilters = "contacts" | "work-type" | "users" | "status";

type Props = {
  filters: WorksFilters;
  setFilters: React.Dispatch<React.SetStateAction<WorksFilters>>;

  clientOptions?: SelectOptionType[];
  workTypeOptions?: SelectOptionType[];
  statusesOptions?: SelectOptionType[];
  usersOptions?: SelectOptionType[];

  isLoadingContacts?: boolean;
  isLoadingWorkTypes?: boolean;
  isLoadingStatuses?: boolean;
  isLoadingUsers?: boolean;

  searchClients?: string;
  setSearchClients?: (value: string) => void;

  searchWorkTypes?: string;
  setSearchWorkTypes?: (value: string) => void;

  onOpenFiltersModal?: () => void;

  showFilters: ShowFilters[];
  showFiltersModal?: boolean;

  className?: string;
};

export const WorkFiltersBase: FC<Props> = ({
  filters,
  setFilters,
  clientOptions = [],
  workTypeOptions = [],
  statusesOptions = [],
  usersOptions = [],
  isLoadingContacts,
  isLoadingWorkTypes,
  isLoadingStatuses,
  isLoadingUsers,
  searchClients = "",
  setSearchClients,
  searchWorkTypes = "",
  setSearchWorkTypes,
  showFilters,
  className,
  onOpenFiltersModal,
  showFiltersModal,
}) => {
  const isShowClearButton = hasActiveWorksFilters(filters);

  const columnsCount = showFilters.length;

  return (
    <div className={classNames("flex items-start gap-4", className)}>
      <div
        className={classNames(
          "grid grid-cols-4 gap-x-4 gap-y-2",
          `grid-cols-[repeat(${columnsCount},max-content)]`,
          columnsCount > 3 &&
            "max-[1780px]:grid-cols-3 max-[1600px]:grid-cols-2",
        )}>
        {showFilters.includes("contacts") && (
          <div className={"min-w-[200px]"}>
            <div className={"mb-0.5 text-sm"}>Client</div>
            <CustomSelect
              options={clientOptions}
              value={filters.customerIds}
              onChange={(newValue) => {
                const value = newValue as SelectOptionType[];
                setFilters((prev) => ({ ...prev, customerIds: value }));
              }}
              inputValue={searchClients}
              onChangeInput={setSearchClients}
              isMulti
              isLoading={isLoadingContacts}
              indicatorIcon={() => <Search size={24} />}
              selectStyle={{ countLabel: "clients", small: true }}
            />
          </div>
        )}

        {showFilters.includes("users") && (
          <div className={"min-w-[200px]"}>
            <div className={"mb-0.5 text-sm"}>Assignee</div>
            <CustomSelect
              options={usersOptions}
              value={filters.userIds}
              onChange={(newValue) => {
                const value = newValue as SelectOptionType[];
                setFilters((prev) => ({ ...prev, userIds: value }));
              }}
              isMulti
              withMenu
              isLoading={isLoadingUsers}
              selectStyle={{ fitChips: true, small: true }}
            />
          </div>
        )}

        {showFilters.includes("work-type") && (
          <div className={"min-w-[200px]"}>
            <div className={"mb-0.5 text-sm"}>Work type</div>
            <CustomSelect
              options={workTypeOptions}
              value={filters.worktypeIds}
              onChange={(newValue) => {
                const value = newValue as SelectOptionType[];
                setFilters((prev) => ({ ...prev, worktypeIds: value }));
              }}
              inputValue={searchWorkTypes}
              onChangeInput={setSearchWorkTypes}
              isMulti
              withMenu
              isLoading={isLoadingWorkTypes}
              placeholder={"All"}
              selectStyle={{ fitChips: true, small: true }}
            />
          </div>
        )}

        {showFilters.includes("status") && (
          <div className={"min-w-[200px]"}>
            <div className={"mb-0.5 text-sm"}>Status</div>
            <CustomSelect
              options={statusesOptions}
              value={filters.workStatuses}
              onChange={(newValue) => {
                const value = Array.isArray(newValue) ? newValue : [newValue];
                setFilters((prev) => ({
                  ...prev,
                  workStatuses: value as SelectOptionType[],
                }));
              }}
              isLoading={isLoadingStatuses}
              withMenu
              selectStyle={{ small: true }}
            />
          </div>
        )}
      </div>

      <div className={"mt-3 flex gap-2"}>
        {showFiltersModal && (
          <Button
            label={"Filter"}
            extraClasses={"flex flex-col"}
            icon={<IoFilterOutline size={"20px"} />}
            onClick={onOpenFiltersModal}
            colorType={"ghost"}
          />
        )}

        {isShowClearButton && (
          <Button
            label={"Clear"}
            onClick={() => setFilters(clearWorksFilters())}
            colorType={"ghost"}
            icon={<HiXMark size={20} />}
          />
        )}
      </div>
    </div>
  );
};
